/**
 * Reports API Client
 * Frontend API calls for financial reports
 */

import apiClient from './client';

/**
 * Get P&L report for a specific branch
 */
export const getBranchPLReport = async (branchId, startDate, endDate) => {
  const response = await apiClient.get(`/reports/pl/branch/${branchId}`, {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Get consolidated P&L across all branches
 */
export const getConsolidatedPLReport = async (startDate, endDate) => {
  const response = await apiClient.get('/reports/pl/consolidated', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Get payroll cost report
 */
export const getPayrollCostReport = async (startDate, endDate, branchId = null) => {
  const params = { startDate, endDate };
  if (branchId) params.branchId = branchId;

  const response = await apiClient.get('/reports/payroll-cost', { params });
  return response.data;
};

/**
 * Get inventory valuation report
 */
export const getInventoryValuationReport = async (branchId = null) => {
  const params = {};
  if (branchId) params.branchId = branchId;

  const response = await apiClient.get('/reports/inventory-valuation', { params });
  return response.data;
};

/**
 * Get sales book (detailed sales report)
 */
export const getSalesBookReport = async (startDate, endDate, branchId = null) => {
  const params = { startDate, endDate };
  if (branchId) params.branchId = branchId;

  const response = await apiClient.get('/reports/sales-book', { params });
  return response.data;
};

// ============================================================
// PDF DOWNLOAD FUNCTIONS
// ============================================================

export const downloadConsolidatedPLPDF = async (startDate, endDate) => {
  const response = await apiClient.get('/reports/pl/consolidated/pdf', {
    params: { startDate, endDate },
    responseType: 'blob'
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Consolidated-PL-${startDate}-${endDate}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadBranchPLPDF = async (branchId, startDate, endDate) => {
  const response = await apiClient.get(`/reports/pl/branch/${branchId}/pdf`, {
    params: { startDate, endDate },
    responseType: 'blob'
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Branch-PL-${branchId}-${startDate}-${endDate}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadInventoryValuationPDF = async (branchId = null) => {
  const params = {};
  if (branchId) params.branchId = branchId;

  const response = await apiClient.get('/reports/inventory-valuation/pdf', {
    params,
    responseType: 'blob'
  });

  const filename = branchId
    ? `Inventory-Valuation-Branch-${branchId}.pdf`
    : 'Inventory-Valuation-All.pdf';
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getStatementOfAccount = async (startDate, endDate, filters = {}) => {
  const response = await apiClient.get('/reports/statement-of-account', {
    params: {
      start_date: startDate,
      end_date: endDate,
      ...filters
    }
  });
  return response.data;
};

export default {
  getBranchPLReport,
  getConsolidatedPLReport,
  getPayrollCostReport,
  getInventoryValuationReport,
  getSalesBookReport,
  getStatementOfAccount,
  downloadConsolidatedPLPDF,
  downloadBranchPLPDF,
  downloadInventoryValuationPDF
};
